// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import SpeedDial from 'presentations/views/components/atoms/SpeedDial';

import {
  mediaMax,
  fontSize,
  spacing,
  isMediaMax,
  zIndex,
  borderRadius,
  fontWeight,
} from 'presentations/views/styles';
import {
  white,
  gray01,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import dynamic from 'next/dynamic';
const NotificationButton = dynamic(() =>
  import('presentations/views/components/organisms/NotificationButton'),
);
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import UserMenuList from 'presentations/views/components/molecules/UserMenuList';
import Logo from 'presentations/views/components/atoms/Logo';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Button from 'presentations/views/components/atoms/Button';
import {
  dashboardPath,
  signUpPath,
  loginPath,
  dashboardSearchPath,
  accountSettingPath,
  adminPaymentPath,
  integrationListPath,
  dashboardCalendarsPath,
} from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';

import { useHeaderDashboard } from './hooks';
import { PlanId } from 'domains/models/subscription/planId';
import { isAndroid, isiOSApp } from 'presentations/views/helpers/electron';
import CreateMenu from 'presentations/views/components/molecules/CreateMenu';
import { useCreateMenu } from 'presentations/views/components/molecules/CreateMenu/hooks';
import { Role } from 'domains/models/team_user/role';
import Subscription from 'domains/models/subscription';
import { trackClickEvent } from 'presentations/views/helpers/analytics';

interface Props {
  viewer: ViewerItem | null;
  isLoading?: boolean;
  isAdmin?: boolean;
  isShowMobileCreateMenu?: boolean;
  mobileZIndex?: number;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const { state, actions } = useHeaderDashboard({
    viewer: props.viewer,
  });
  const { state: createState, actions: createActions } = useCreateMenu({
    viewer: props.viewer,
  });
  const isMobile = isMediaMax('tablet');
  const homeUrl = dashboardPath();
  const helpOptions = [
    {
      icon: 'help',
      label: t('Help'),
    },
    {
      icon: 'message-processing-outline',
      label: t('Chat Support'),
    },
  ];
  const router = useRouter();
  const searchPath = dashboardSearchPath({
    from: router.isReady ? router.asPath : dashboardPath(),
  });
  const isShowUpgrade =
    props.viewer &&
    props.viewer.planId === PlanId.Free &&
    !isiOSApp() &&
    !isAndroid();
  const isDefaultPro =
    !isiOSApp() &&
    !isAndroid() &&
    props.viewer &&
    Subscription.isDefaultFreeTrial(
      props.viewer.trialEndedAt,
      props.viewer.trialChannel,
    );

  return (
    <Container
      isElectron={state.isElectron}
      mobileZIndex={props.mobileZIndex}
      isiOSApp={state.isiOSApp}
    >
      <LeftBox>
        {!state.isElectron && (
          <Link
            href={homeUrl}
            onClick={(e: SyntheticEvent) => {
              if (state.isElectron) {
                e.preventDefault();
                router.replace(homeUrl);
              }
            }}
          >
            <ServiceLogo isDarkable width={130} height={34} />
          </Link>
        )}
        {state.isElectron && (
          <AppNavigation>
            <ElectronHistoryListItem>
              <ElectronHistoryButton
                onClick={() => window.history.back()}
                size="small"
                color="inherit"
                variant="text"
              >
                <Icon name="arrow-left" size={fontSize(20)} />
              </ElectronHistoryButton>
            </ElectronHistoryListItem>
            <ElectronHistoryListItem>
              <ElectronHistoryButton
                onClick={() => window.history.forward()}
                size="small"
                color="inherit"
                variant="text"
              >
                <Icon name="arrow-right" size={fontSize(20)} />
              </ElectronHistoryButton>
            </ElectronHistoryListItem>
          </AppNavigation>
        )}
        {!isMobile && (
          <NavigationContainer isElectron={state.isElectron}>
            {props.viewer && (
              <NavigationList>
                <NavigationListItem>
                  <NavigationListItemTitle
                    active={state.activePath.calendar}
                    href={dashboardCalendarsPath()}
                    onClick={createActions.onClickCreateMenu}
                  >
                    <CreateButton isElectron={state.isElectron}>
                      <AddIcon name="add" />
                      {t('Create')}
                    </CreateButton>
                  </NavigationListItemTitle>
                </NavigationListItem>
                <NavigationListItem>
                  <NavigationListItemTitle
                    active={state.activePath.dashboard}
                    href={dashboardPath()}
                    onClick={trackClickEvent}
                    dataProps={{
                      ['data-event-name']: 'dashboard header click home',
                    }}
                  >
                    {t('Home')}
                  </NavigationListItemTitle>
                </NavigationListItem>
                <NavigationListItem>
                  <NavigationListItemTitle
                    active={state.activePath.calendar}
                    href={dashboardCalendarsPath()}
                    onClick={trackClickEvent}
                    dataProps={{
                      ['data-event-name']: 'dashboard header click calendar',
                    }}
                  >
                    {t('Calendar')}
                  </NavigationListItemTitle>
                </NavigationListItem>
                <NavigationListItem>
                  <NavigationListItemTitle
                    active={state.activePath.integration}
                    href={integrationListPath()}
                    onClick={trackClickEvent}
                    dataProps={{
                      ['data-event-name']:
                        'dashboard header click integrations',
                    }}
                  >
                    {t('Integration')}
                  </NavigationListItemTitle>
                </NavigationListItem>
                <NavigationListItem>
                  <NavigationListItemTitle
                    active={state.activePath.setting}
                    href={accountSettingPath()}
                    onClick={trackClickEvent}
                    dataProps={{
                      ['data-event-name']: 'dashboard header click settings',
                    }}
                  >
                    {t('Setting')}
                  </NavigationListItemTitle>
                </NavigationListItem>
              </NavigationList>
            )}
          </NavigationContainer>
        )}
      </LeftBox>
      <RightBox>
        <MenuList>
          {props.isLoading ? null : props.viewer ? (
            <>
              {isShowUpgrade ? (
                <MenuListItem>
                  <PaidLink href={adminPaymentPath()} target="_blank">
                    <PaidIcon name="trophy-variant" size={fontSize(24)} />
                    {t('Upgrade')}
                  </PaidLink>
                </MenuListItem>
              ) : (
                isDefaultPro && (
                  <MenuListItem>
                    <PaidLink href={adminPaymentPath()} target="_blank">
                      <PaidIcon name="trophy-variant" size={fontSize(24)} />
                      {t('Trial')}
                    </PaidLink>
                  </MenuListItem>
                )
              )}

              {isMobile && props.isShowMobileCreateMenu && (
                <StyledSpeedDial
                  open={!!createState.createMenuAnchor}
                  onClick={createActions.onClickCreateMenu}
                />
              )}

              {props.viewer.role < Role.Restricted &&
                props.viewer.planId >= PlanId.Pro && (
                  <MenuListItem>
                    <IconLink href={searchPath} color="textPrimary">
                      <Icon name="search" size={fontSize(24)} />
                    </IconLink>
                  </MenuListItem>
                )}
              <MenuListItem>
                <NotificationButton viewer={props.viewer} />
              </MenuListItem>
              <MenuListItem>
                <IconLink onClick={actions.onClickHelpMenu} color="textPrimary">
                  <Icon name="help" size={fontSize(24)} />
                </IconLink>
              </MenuListItem>

              <MenuListItem data-intercom-target="header-user-menu-list">
                <UserMenuList viewer={props.viewer} />
              </MenuListItem>
            </>
          ) : (
            <>
              <MenuListItem>
                <Button
                  href={loginPath({
                    to: state.to,
                    locale: router.locale,
                  })}
                  withoutNextLink
                  color="inherit"
                  variant="text"
                >
                  {t('Sign In')}
                </Button>
              </MenuListItem>
              <MenuListItem>
                <Button
                  href={signUpPath({
                    to: state.to,
                    locale: router.locale,
                  })}
                  withoutNextLink
                  color="primary"
                >
                  {t('Sign Up')}
                </Button>
              </MenuListItem>
            </>
          )}
        </MenuList>
      </RightBox>
      <FloatSettingMenu
        onCloseMenu={actions.onCloseHelpMenu}
        onSelectMenu={actions.onSelectHelpMenu}
        anchor={state.helpAnchor}
        options={helpOptions}
      />
      {props.viewer && (
        <CreateMenu
          anchor={createState.createMenuAnchor}
          onCloseMenu={createActions.onCloseCreateMenu}
          onClickNewEventType={createActions.onClickNewEventType}
          onClickNewForm={createActions.onClickNewForm}
          isShowingPlanUpgrade={createState.isShowingPlanUpgrade}
          onClosePlanUpgrade={createActions.onClosePlanUpgrade}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          viewer={props.viewer}
        />
      )}
    </Container>
  );
});

const Container = styled.div<{
  isElectron: boolean;
  mobileZIndex?: number;
  isiOSApp: boolean;
}>`
  display: flex;
  align-items: center;
  height: 64px;
  position: relative;
  background-color: ${white};
  justify-content: space-between;
  -webkit-app-region: drag;
  ${mediaMax.tablet} {
    height: 54px;
    /* z-index: ${zIndex(1300)}; */

    ${({ mobileZIndex }) =>
      mobileZIndex &&
      css`
        z-index: ${mobileZIndex};
      `}
  }
  ${({ isElectron }) =>
    isElectron &&
    css`
      height: 42px;
    `}
  ${({ isiOSApp }) =>
    isiOSApp &&
    css`
      display: none;
    `}
`;
const LeftBox = styled.div`
  /* margin-right: auto; */
  margin-left: ${spacing(16)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-right: initial;
  }
  ${mediaMax.xs} {
    overflow: hidden;
  }
`;

const NavigationContainer = styled.div<{
  isElectron: boolean;
}>`
  ${({ isElectron }) =>
    !isElectron &&
    css`
      margin-left: ${spacing(32)};
    `}
`;
const RightBox = styled.div`
  margin-right: ${spacing(16)};
  ${mediaMax.tablet} {
    width: 48px;
  }
`;
const AppNavigation = styled.ul`
  margin-left: ${spacing(64)};
  margin-right: ${spacing(16)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ServiceLogo = styled(Logo)`
  ${mediaMax.tablet} {
    width: 89px;
  }
`;
const IconLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${gray01};
  :hover {
    text-decoration: none;
  }
`;
const MenuList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const MenuListItem = styled.li`
  margin-right: ${spacing(20)};
  color: ${gray01};
  :last-of-type {
    margin-right: 0;
  }
`;
const ElectronHistoryListItem = styled.li`
  margin-right: ${spacing(8)};

  &:last-of-type {
    margin-right: 0;
  }
`;
const ElectronHistoryButton = styled(Button)`
  min-width: 24px;
  padding: ${spacing(4)};
  color: ${gray01};
`;
export const PaidIcon = styled(Icon)`
  width: 14px !important;
  height: 14px !important;
  margin-right: ${spacing(4)};
`;
export const PaidLink = styled(Link)`
  border-radius: ${borderRadius(6)};
  border: 1px solid ${primary};
  padding: ${spacing(4)} ${spacing(6)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(12)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;

const StyledSpeedDial = styled(SpeedDial)`
  position: fixed;
  bottom: 72px;
  width: 48px;
  height: 48px;
`;

const NavigationList = styled.div`
  display: flex;
  align-items: center;
`;
const NavigationListItem = styled.div`
  margin-right: ${spacing(24)};
  display: flex;
  align-items: center;

  :last-of-type {
    margin-right: 0;
  }
`;
const NavigationListItemTitle = styled(Link)<{ active?: boolean }>`
  font-weight: ${fontWeight('bold')};
  color: ${({ active }) => (active ? primary : gray01)};
`;

const CreateButton = styled.div<{ isElectron: boolean }>`
  background-color: ${primary};
  color: ${whiteFix};
  height: 34px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  padding: ${spacing(8)} ${spacing(16)};
  font-weight: ${fontWeight('bold')};

  ${({ isElectron }) =>
    isElectron
      ? css`
          height: 26px;
          padding: ${spacing(4)} ${spacing(12)};
          font-size: ${fontSize(12)};
        `
      : css`
          padding: ${spacing(8)} ${spacing(16)};
        `}
`;
const AddIcon = styled(Icon)`
  font-size: ${fontSize(16)};
  margin-right: 2px;
`;

export default Component;
